import { Component } from 'react';
import { locationShape, routerShape } from 'react-router';
import { connect } from 'react-redux';
import { replace, goBack, push } from 'react-router-redux';
import lodash from 'lodash';

import {
    addApplicationHeaderTitle
} from 'common/actions/applicationHeader';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

import {
    loadSdfaInboxItem,
    clearSdfaItem,
    submitSdfaSignTask
} from 'forms/actions/sdfa';
import {
    sortSelector,
    querySelector,
    orderSelector,
} from 'workflow/selectors/inbox';

import { SubFormRouter, SubFormRoute } from 'forms/containers/SubFormRouter/SubFormRouter';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import SdfaInboxItem from 'forms/components/Sdfa/InboxItem';
import SignForm from 'forms/components/Sdfa/SignForm/SignForm';
import WorkflowDocumentViewer from 'workflow/containers/WorkflowDocumentViewer';

export class SdfaApproveAndSign extends Component {
    constructor(props) {
        super(props);
        this.state = {
            taskActionSent: false,
            forceRerender: false,
        };
    }

    componentDidMount() {
        this.loadCurrentSdfaItem();
    }

    componentWillUnmount() {
        this.props.clearSdfaItem();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.pageTitle === null && nextProps.inboxItem) {
            this.props.addApplicationHeaderTitle(nextProps.inboxItem.name);
        }
    }

    componentWillUpdate(nextProps) {
        if (nextProps.taskId !== this.props.taskId) {
            this.props.clearSdfaItem();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.taskId !== this.props.taskId) {
            this.loadCurrentSdfaItem();
        }
        const { submissionSuccess } = this.props;
        if (submissionSuccess && prevProps.submissionSuccess !== submissionSuccess) {
            this.onSubmissionSuccess();
        }
        if (this.state.forceRerender) {
            this.setState({ forceRerender: false });
        }
    }

    loadCurrentSdfaItem() {
        this.props.loadSdfaInboxItem(this.props.taskId);
    }

    onSubmissionSuccess() {
        const { replace, inboxItem } = this.props;
        const { nextTask } = inboxItem;
        const { sort, order, query, location } = this.props;
        const newLocation = lodash.cloneDeep(location);
        newLocation.query.sort = sort;
        newLocation.query.order = order;
        newLocation.query.q = query;
        delete newLocation.query.subformroute;
        newLocation.pathname = nextTask ? `/form/task/${nextTask}` : '/inbox';
        this.setState({ taskActionSent: false, forceRerender: true });
        replace(newLocation);
    }

    onActionSubmit() {
        const { push, location } = this.props;
        const updatedLocation = lodash.cloneDeep(location);
        updatedLocation.query.subformroute = 'signDocument';
        push(updatedLocation);
    }

    viewDocument() {
        const { push, location } = this.props;
        const updatedLocation = lodash.cloneDeep(location);
        updatedLocation.query.subformroute = 'viewDocument';
        push(updatedLocation);
    }

    viewDocumentOnNewTab() {
        const { location } = this.props;
        const subRoute = '?subformroute=viewDocument';
        window.open(`${location.pathname}${subRoute}`, '_blank');
    }

    onSignDocumentSubmit(signingToken) {
        this.setState({
            taskActionSent: true
        }, this.signDocument.bind(this, signingToken));
    }

    signDocument(signingToken) {
        const { inboxItem, submitSdfaSignTask } = this.props;
        submitSdfaSignTask(inboxItem.id, signingToken);
    }

    render() {
        const {
            inboxItem,
            document,
            signingStatement,
            originUrl,
            taskId
        } = this.props;

        if (this.state.forceRerender || lodash.isNull(inboxItem)) {
            return <LoadingComponent />;
        }

        if (this.state.taskActionSent) {
            const loadingText = 'Completing task ...';
            return (
                <LoadingComponent
                    text={loadingText} />
            );
        }

        return (
            <SubFormRouter data-cy="ApproveAndSign">
                <SubFormRoute>
                    <SdfaInboxItem
                        taskId={taskId}
                        inboxItem={inboxItem}
                        document={document}
                        actionText='Select Document and Sign'
                        onActionClicked={this.onActionSubmit.bind(this)}
                        viewDocument={this.viewDocument.bind(this)}
                        data-cy="ApproveAndSign-onActionClicked" />
                </SubFormRoute>
                <SubFormRoute query="viewDocument">
                    <WorkflowDocumentViewer taskId={taskId} />
                </SubFormRoute>
                <SubFormRoute query="signDocument">
                    <SignForm
                        originUrl={originUrl}
                        document={document}
                        signingStatement={signingStatement}
                        viewDocument={this.viewDocumentOnNewTab.bind(this)}
                        onSignDocumentSubmit={this.onSignDocumentSubmit.bind(this)}
                        addApplicationMessage={this.props.addApplicationMessage}
                        isSSOSigningEnabled={Boolean(lodash.get(this.props.featureAccess, 'ssoESign'))}
                        isSsoUser={lodash.get(this.props.user, 'isSso', false)} />
                </SubFormRoute>
            </SubFormRouter>
        );
    }
}

SdfaApproveAndSign.contextTypes = {
    router: routerShape.isRequired,
    location: locationShape.isRequired
};

export default connect((state, props) => {
    const inboxItem = lodash.get(state, 'form.inboxItem', null);
    const document = lodash.get(state, 'form.inboxItem.document', null);
    const signingStatement = lodash.get(state, 'form.inboxItem.signingStatement.statement', null);
    const taskId = lodash.get(props, 'params.id', null);
    const actionText = lodash.get(state, 'form.inboxItem.actionName', '');
    const originUrl = lodash.get(state, 'application.config.corsOriginUrl', null);
    const submissionSuccess = lodash.get(state, 'form.submissionSuccess', null);
    const pageTitle = lodash.get(state, 'app.pageTitle', null);
    return {
        inboxItem,
        document,
        signingStatement,
        taskId,
        originUrl,
        submissionSuccess,
        pageTitle,
        user: state.me,
        featureAccess: lodash.get(state, `me.featureAccess.${lodash.get(state, 'me.institution.id')}`, {}),
        actionText: lodash.startCase(actionText),
        query: querySelector(state),
        sort: sortSelector(state),
        order: orderSelector(state),
    };
}, {
    loadSdfaInboxItem,
    clearSdfaItem,
    submitSdfaSignTask,
    addApplicationMessage,
    addApplicationHeaderTitle,
    replace,
    goBack,
    push
})(SdfaApproveAndSign);
